import React, { useMemo } from 'react'
import { connect } from 'react-redux'
import { createStructuredSelector } from 'reselect'
import { ClearingHouse, EntityType, Product, ProductSector, Sponsor, TradingEntityClearing, TradingEntityClearingType } from 'api'
import { ModalFooter, ModalWindow } from 'view/components/panels'
import { DialogField, DialogRow, TextInputField } from 'view/components/inputs'
import { TextButton, TextButtonType } from 'view/components/buttons'
import { SelectboxField } from 'view/components/options'
import { HorizontalLayout } from 'view/styles/constants'
import { OnOffProductsPanelGrid, OnOffProductsPanelGridRow } from 'view/shared/datagrid/products/OnOffProductsPanelGrid'
import { productRecordSelector, clearableProductSectorRecordSelector } from 'selectors/productSelectors'
import { createAllEntitiesSelector } from 'selectors/entitySelectors'
import { SponsorHelper } from 'model'
import { Locale, Translation } from 'locale'
import { noop } from 'lodash'
import { tradingEntityClearingByIdGetterSelector } from 'selectors/clearingSelectors'
import { Content, GridWrapper } from './TradingEntityClearingDialogStyled'
import { getProductsRowData } from './tradingEntityClearingDialogHelpers'

interface ViewTradingEntityDirectClearingDialogProps {
  // Own props
  tradingEntityClearingType: TradingEntityClearingType
  entityId: string
  onClose?: () => unknown

  //State props
  getTradingEntityClearingEntity: (entityId: string) => TradingEntityClearing | undefined
  productSectors: Record<string, ProductSector>
  products: Record<string, Product>
  clearingHouses: ClearingHouse[]
  sponsors: Sponsor[]
}

const mapStateToProps = createStructuredSelector({
  getTradingEntityClearingEntity: tradingEntityClearingByIdGetterSelector,
  productSectors: clearableProductSectorRecordSelector,
  products: productRecordSelector,
  clearingHouses: createAllEntitiesSelector(EntityType.CLEARING_HOUSE),
  sponsors: createAllEntitiesSelector(EntityType.SPONSOR)
})

const ViewTradingEntityDirectClearingDialogComponent: React.FunctionComponent<ViewTradingEntityDirectClearingDialogProps> = props => {
  const {
    tradingEntityClearingType,
    entityId,
    onClose,
    getTradingEntityClearingEntity,
    productSectors,
    products,
    clearingHouses,
    sponsors
  } = props

  const tradingEntityClearing = getTradingEntityClearingEntity(entityId)

  const productsRowData = useMemo(
    (): OnOffProductsPanelGridRow[] => (tradingEntityClearing ? getProductsRowData(tradingEntityClearing, productSectors, products) : []),
    [tradingEntityClearing, productSectors, products]
  )

  const handleCancel = () => {
    if (onClose) {
      onClose()
    }
  }

  if (!tradingEntityClearing) {
    return null
  }

  const title =
    tradingEntityClearing.type === TradingEntityClearingType.DIRECT
      ? Translation.TC_BUTTON_VIEW_DIRECT_TRADING_ENTITY_CLEARING
      : Translation.TC_BUTTON_VIEW_SPONSORED_TRADING_ENTITY_CLEARING

  return (
    <ModalWindow dataTestId={'TradingEntityClearingDialog-ModalWindow-Title'} title={Locale.translate(title)}>
      <Content>
        <DialogRow horizontalLayout={HorizontalLayout.Left}>
          <DialogField>
            <SelectboxField
              dataTestId={'TradingEntityClearingDialog-SelectboxField-CCP'}
              value={tradingEntityClearing.clearingHouseId}
              label={Locale.translate(Translation.TC_CCP)}
              optionKeys={[]}
              getOption={(clearingHouseId: string) => clearingHouses.find(clearingHouse => clearingHouse.id === clearingHouseId)}
              getOptionText={(clearingHouse: ClearingHouse) => clearingHouse.publishedName}
              onUpdate={noop}
              readOnly={true}
            />
          </DialogField>
        </DialogRow>
        <DialogRow horizontalLayout={HorizontalLayout.Left}>
          <DialogField>
            <TextInputField
              dataTestId={'TradingEntityClearingDialog-TextInputField-Code'}
              value={tradingEntityClearing.swiftMessageId}
              label={Locale.translate(Translation.TC_CODE)}
              onUpdate={noop}
              readOnly={true}
            />
          </DialogField>
          {tradingEntityClearingType === TradingEntityClearingType.SPONSORED && (
            <DialogField>
              <SelectboxField
                dataTestId={'TradingEntityClearingDialog-SelectboxField-SponsoringBank'}
                value={tradingEntityClearing.sponsorId}
                label={Locale.translate(Translation.TC_SPONSORING_BANK)}
                optionKeys={[]}
                getOption={(sponsorId: string) => sponsors.find(sponsor => sponsor.id === sponsorId)}
                getOptionText={SponsorHelper.getName}
                onUpdate={noop}
                readOnly={true}
              />
            </DialogField>
          )}
        </DialogRow>
        <DialogRow horizontalLayout={HorizontalLayout.Left}>
          <GridWrapper height={400}>
            <OnOffProductsPanelGrid rowData={productsRowData} onUpdate={noop} disabled={true} />
          </GridWrapper>
        </DialogRow>
      </Content>
      <ModalFooter>
        <DialogRow horizontalLayout={HorizontalLayout.Right}>
          <DialogField flex={0}>
            <TextButton dataTestId={'TradingEntityClearingDialog-TextButton-ButtonCancel'} type={TextButtonType.Bad} onClick={handleCancel}>
              {Locale.translate(Translation.TC_BUTTON_CANCEL)}
            </TextButton>
          </DialogField>
        </DialogRow>
      </ModalFooter>
    </ModalWindow>
  )
}

export const ViewTradingEntityDirectClearingDialog = connect(mapStateToProps)(ViewTradingEntityDirectClearingDialogComponent)
